import { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { getEffectDefinition } from '@/vfx/registry';
import {
  clampVfxParam,
  formatVfxParamValue,
  vfxParamSliderMax,
  vfxParamSliderMin,
  vfxParamSliderStep,
  vfxParamToSliderValue,
  vfxSliderValueToParam,
} from '@/lib/vfxParameterSlider';

function resolveParamValue(clip, def, schema) {
  const raw = clip.parameters?.[schema.key];
  return clampVfxParam(
    typeof raw === 'number' ? raw : (def.defaultParameters[schema.key] ?? schema.min),
    schema,
  );
}

function buildParamsFromClip(clip, def) {
  const params = {};
  def.parameterSchema.forEach((schema) => {
    params[schema.key] = resolveParamValue(clip, def, schema);
  });
  return params;
}

/** Keyframe-ready parameter inspector for the selected effect clip. */
export function EffectInspectorPanel({ clip, currentTime, onUpdate, onClose }) {
  const def = getEffectDefinition(clip?.type);

  const [draftParams, setDraftParams] = useState(() => (
    def && clip ? buildParamsFromClip(clip, def) : {}
  ));

  useEffect(() => {
    if (!clip?.id || !def) return;
    setDraftParams(buildParamsFromClip(clip, def));
  }, [clip?.id]);

  const setParam = useCallback((key, value, schema) => {
    const clamped = schema ? clampVfxParam(value, schema) : value;
    setDraftParams((prev) => ({ ...prev, [key]: clamped }));
    const patch = { parameters: { [key]: clamped } };
    if (clip?.keyframeTracks?.[key]?.length) {
      const tracks = { ...clip.keyframeTracks };
      delete tracks[key];
      patch.keyframeTracks = tracks;
    }
    onUpdate(patch);
  }, [clip?.keyframeTracks, onUpdate]);

  if (!clip) {
    return (
      <div className="text-xs p-3" style={{ color: 'var(--text-tertiary)' }}>
        Select an effect on the timeline to edit parameters.
      </div>
    );
  }

  if (!def) return null;

  const addKeyframe = (key, value, schema) => {
    const localTime = Math.max(0, currentTime - clip.startTime);
    const tracks = { ...(clip.keyframeTracks || {}) };
    const existing = tracks[key] || [];
    const filtered = existing.filter((k) => Math.abs(k.time - localTime) > 0.05);
    const clamped = schema ? clampVfxParam(value, schema) : value;
    tracks[key] = [...filtered, { time: localTime, value: clamped }].sort((a, b) => a.time - b.time);
    const nextParams = { ...draftParams, [key]: clamped };
    setDraftParams(nextParams);
    onUpdate({
      keyframeTracks: tracks,
      parameters: nextParams,
    });
  };

  return (
    <div className="p-3 space-y-3" data-testid="vfx-inspector-panel">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
          {def.label}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <Label className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>Enabled</Label>
          <Switch
            checked={clip.enabled !== false}
            onCheckedChange={(v) => onUpdate({ enabled: v })}
          />
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 rounded-md flex items-center justify-center border transition hover:bg-white/10"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
              title="Close"
              aria-label="Close VFX settings"
              data-testid="vfx-inspector-close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : null}
        </div>
      </div>
      {def.parameterSchema.map((schema) => {
        const val = draftParams[schema.key] ?? resolveParamValue(clip, def, schema);
        const sliderValue = vfxParamToSliderValue(val, schema);
        return (
          <div key={schema.key} className="space-y-1">
            <div className="flex justify-between items-center">
              <Label className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                {schema.label}
              </Label>
              <button
                type="button"
                className="text-[9px] px-1.5 py-0.5 rounded border"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-tertiary)' }}
                onClick={() => addKeyframe(schema.key, val, schema)}
                title="Add keyframe at playhead"
              >
                ◆ Keyframe
              </button>
            </div>
            <Slider
              value={[sliderValue]}
              min={vfxParamSliderMin(schema)}
              max={vfxParamSliderMax(schema)}
              step={vfxParamSliderStep(schema)}
              onValueChange={([v]) => setParam(schema.key, vfxSliderValueToParam(v, schema), schema)}
            />
            <span className="text-[9px]" style={{ color: 'var(--text-tertiary)' }}>
              {formatVfxParamValue(val, schema)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default EffectInspectorPanel;
